/* screens/academy/StudentsListTab.jsx — глобальная вкладка «Ученики» консоли Академии (B4).
   Методист (academy_manage) видит всех учеников Академии: ФИО, email, телефон, число
   курсов. Данные — GET /api/academy/manage/students/ (academy.listStudents) с
   LimitOffset-пагинацией (limit/offset) и поиском q (email/ФИО/телефон).
   Контракт ответа (contract-B4-get-api-academy-manage-students.json, S13 — читаем ровно эти
   ключи): { count, next, previous, results:[{ id, name, email, phone, enrollment_count,
   enrollments:[{ id, course_id, course_title, status, progress_pct, enrolled_at, tariff }] }] }.
   Гейт правом academy_manage делает родитель (AcademyConsole). Клик по строке открывает
   Drawer с курсами ученика — данные уже есть в enrollments[], без доп. запроса.
   Без raw HTML; ссылки только mailto:/tel:; иконки только из реестра Icon. */
import React from 'react';
import { QueryBoundary, EmptyState } from '../../components/patterns/States.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Pill } from '../../components/ui/Badge.jsx';
import { SearchInput } from '../../components/ui/Field.jsx';
import { Drawer } from '../../components/ui/Drawer.jsx';
import { Avatar } from '../../components/ui/Avatar.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { hooks } from '../../hooks/index.jsx';
import { fmt } from '../../lib/format.js';
import * as api from '../../lib/api/academy.js';

const { useState } = React;
const { useFakeQuery } = hooks;

const PAGE_SIZE = 20;

// статус записи на курс → подпись + тон (контракт: active|completed|expired)
const ENR_STATUS = {
  active: { label: 'Активна', tone: 'info' },
  completed: { label: 'Завершён', tone: 'ok' },
  expired: { label: 'Истёк', tone: 'neutral' },
};
function enrStatusMeta(status) {
  return ENR_STATUS[status] || { label: status || '—', tone: 'neutral' };
}

// ── дровер с курсами ученика (из уже загруженного enrollments[]) ──────────────────
function StudentCoursesDrawer({ student, onClose }) {
  if (!student) return null;
  const enrollments = Array.isArray(student.enrollments) ? student.enrollments : [];
  return (
    <Drawer open level={2} onClose={onClose} size="md"
      title={student.name} breadcrumbs="Курсы ученика">
      <div className="stack stack-6">
        {/* ── контакты ── */}
        <section className="acad-section">
          <div className="acad-student-card__head">
            <Avatar label={student.name} size={44} />
            <div className="stack stack-1" style={{ minWidth: 0 }}>
              <span className="acad-student-card__name">{student.name}</span>
              {student.email && (
                <a className="acad-student-card__contact" href={`mailto:${student.email}`}>
                  <Icon name="mail" size={13} />{student.email}
                </a>
              )}
              {student.phone && (
                <a className="acad-student-card__contact" href={`tel:${student.phone}`}>
                  <Icon name="user" size={13} />{student.phone}
                </a>
              )}
            </div>
          </div>
        </section>

        {/* ── курсы ученика ── */}
        <section className="acad-section">
          <div className="acad-section__head">
            <Icon name="layers" size={15} />
            <span className="acad-section__title">Курсы ученика</span>
          </div>
          {enrollments.length === 0
            ? <span className="t-caption text-mute">Записей на курсы нет.</span>
            : (
              <div className="stack stack-2">
                {enrollments.map((e) => {
                  const sm = enrStatusMeta(e.status);
                  return (
                    <div key={e.id} className="acad-sd-course">
                      <span className="acad-sd-course__title" title={e.course_title}>{e.course_title}</span>
                      {e.tariff?.title && (
                        <span className="t-caption text-mute">{e.tariff.title}</span>
                      )}
                      <span className="t-caption text-mute">Записан: {fmt.date(e.enrolled_at)}</span>
                      <span className="t-caption num text-mute">{e.progress_pct ?? 0}%</span>
                      <Pill tone={sm.tone} size="sm" glyph={false}>{sm.label}</Pill>
                    </div>
                  );
                })}
              </div>
            )}
        </section>
      </div>
    </Drawer>
  );
}

function StudentsListTab() {
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState(null); // выбранный ученик для дровера

  const key = [q, offset].join('|');
  const studentsQ = useFakeQuery('acad-students-' + key,
    () => api.listStudents({ q, limit: PAGE_SIZE, offset }), [key]);

  const data = studentsQ.data || {};
  const rows = Array.isArray(data.results) ? data.results : [];
  const count = typeof data.count === 'number' ? data.count : rows.length;

  // поиск меняет q → сбрасываем offset на первую страницу
  const onSearch = (v) => { setQ(v); setOffset(0); };

  const hasPrev = offset > 0;
  const hasNext = offset + rows.length < count;
  const pageFrom = count === 0 ? 0 : offset + 1;
  const pageTo = offset + rows.length;
  const prevPage = () => setOffset((o) => Math.max(0, o - PAGE_SIZE));
  const nextPage = () => setOffset((o) => o + PAGE_SIZE);

  return (
    <>
      <div className="acad-filters">
        <label className="acad-filter ord-search">
          <span className="acad-filter__lbl">Поиск</span>
          <SearchInput value={q} onDebouncedChange={onSearch}
            placeholder="Поиск по email или ФИО" />
        </label>
      </div>

      <QueryBoundary loading={studentsQ.loading} error={studentsQ.error}
        empty={!rows.length} onRetry={studentsQ.reload}
        forbiddenBody="У вас нет прав на просмотр учеников Академии."
        emptyEl={q
          ? <EmptyState serif={false} icon="search" title="Учеников не найдено"
              body="По запросу ничего не нашлось. Измените или очистите поиск."
              action={<Button variant="secondary" iconLeft="close" onClick={() => onSearch('')}>Очистить поиск</Button>} />
          : <EmptyState icon="user" title="Учеников пока нет"
              body="Здесь появятся ученики, записанные на курсы Академии." />}>
        <div className="ord-count t-caption text-mute">Всего учеников: <span className="num">{count}</span></div>
        <div className="cv-table-wrap">
          <table className="cv-table">
            <thead>
              <tr>
                <th>Ученик</th>
                <th>Email</th>
                <th>Телефон</th>
                <th className="num">Курсов</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id} className="acad-students-row"
                  style={{ cursor: 'pointer' }}
                  tabIndex={0} role="button"
                  aria-label={`Курсы ученика ${s.name}`}
                  onClick={() => setSelected(s)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelected(s); } }}>
                  <td data-label="Ученик"><span className="cv-strong">{s.name}</span></td>
                  <td data-label="Email" className="text-dim">{s.email || '—'}</td>
                  <td data-label="Телефон" className="text-dim">{s.phone || '—'}</td>
                  <td data-label="Курсов" className="num">{s.enrollment_count ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {(hasPrev || hasNext) && (
          <div className="acad-students-pager cluster cluster-4" style={{ justifyContent: 'center', marginTop: 'var(--space-8)' }}>
            <Button variant="ghost" size="sm" iconLeft="chevron-left"
              onClick={prevPage} disabled={!hasPrev}>Назад</Button>
            <span className="t-caption text-mute num">{pageFrom}–{pageTo} из {count}</span>
            <Button variant="ghost" size="sm" iconRight="chevron-right"
              onClick={nextPage} disabled={!hasNext}>Вперёд</Button>
          </div>
        )}
      </QueryBoundary>

      {selected && <StudentCoursesDrawer student={selected} onClose={() => setSelected(null)} />}
    </>
  );
}

export { StudentsListTab };
